import React, { useCallback, useState, useRef } from 'react';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { Button } from '~/UIKit';
import { useLanguage, useDateFns } from '~/Hooks';
import { useMediaQuery, Theme } from '@mui/material';
import { MonthSelector } from './MonthSelector';
import { MonthRangePickerStyles } from './MonthRangePicker.styles';
import { PopoverToModal } from '~/Components';

interface IMonthRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  onChange?: Function;
}

type DateObject = {
  start?: Date | undefined;
  end?: Date | undefined;
};

export const MonthRangePicker = ({ startDate, endDate, onChange }: IMonthRangePickerProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { classes } = MonthRangePickerStyles();
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(endDate);
  const [result, setResult] = useState<DateObject>({});
  const { formatDate } = useDateFns();

  const inputRef = useRef(null);
  const { t } = useLanguage();
  const [isFilterModalOpened, setIsFilterModalOpened] = useState(false);

  const onCloseHandler = () => {
    setIsFilterModalOpened(false);
    setStart(startDate);
    setEnd(endDate);
  };

  const onApplyHandler = () => {
    setIsFilterModalOpened(false);
    let { start, end } = result;
    if (!!start && !end) {
      end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      setEnd(end);
    }
    if (!!start && !!end && onChange) {
      onChange({ start, end });
    }
  };

  const handleFilterClick = () => {
    setIsFilterModalOpened(true);
  };

  const getMonthValue = useCallback(
    (date?: Date) => (!date ? '-' : formatDate(date.toDateString(), 'LLL yyyy')),
    [],
  );

  const onDateChange = useCallback(
    (date) => {
      let obj: DateObject = { start: undefined, end: undefined };
      if (!start && !end) {
        setStart(date);
        obj = { start: date };
      } else if (start && !end) {
        if (date < start) {
          setEnd(start);
          setStart(date);
          obj = { start: date, end: start };
        } else if (date > start) {
          setEnd(date);
          obj = { start, end: date };
        } else if (date.getTime() === start.getTime()) {
          setStart(date);
          setEnd(date);
          obj = { start: date, end: date };
        }
      } else if (start && end) {
        if (date < start) {
          setStart(date);
          obj = { start: date, end };
        } else if (date > end) {
          setEnd(date);
          obj = { start, end: date };
        } else if (date > start && date < end) {
          setStart(undefined);
          setEnd(undefined);
        } else if (date.getTime() === start.getTime() || date.getTime() === end.getTime()) {
          setStart(date);
          setEnd(date);
          obj = { start: date, end: date };
        }
      }
      if (obj.end) {
        obj.end = new Date(obj.end.getFullYear(), obj.end.getMonth() + 1, 0);
      }
      setResult(obj);
    },
    [start, end],
  );

  const monthPickerComponent = (
    <div className={classes.wrapper} data-testid="calendar-filter">
      <MonthSelector startDate={start} endDate={end} onDateChange={onDateChange} />
      {!isMobile && (
        <Button
          onClick={onApplyHandler}
          size="small"
          variant="contained"
          className={classes.button}
        >
          {t('app.filter.apply')}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div
        className={classes.inputWraper}
        ref={inputRef}
        onClick={handleFilterClick}
        data-testid="calendar-button"
      >
        <div className={classes.dateFieldWrapper}>
          <span className={classes.dateField}>{getMonthValue(start)}</span>
          <span className={classes.dateField}>{getMonthValue(end)}</span>
        </div>
        <div className={classes.iconWrapper}>
          <CalendarTodayOutlined />
        </div>
      </div>
      <PopoverToModal
        onActionTaken={onApplyHandler}
        open={isFilterModalOpened}
        width="100%"
        anchorEl={inputRef.current}
        onClose={onCloseHandler}
        title={t('app.payouts.modalTitle')}
        actionName={t('app.filter.apply')}
      >
        {monthPickerComponent}
      </PopoverToModal>
    </>
  );
};
